import { Observable } from 'rxjs';
import {
    ExcelDataNormalized,
    ExcelV2Response,
    ExcelV2AgenciamientoRow,
    ExcelV2TransporteRow,
} from './models/liquidaciones.models';
import { resolveServiciosExtraRows } from './liquidaciones-extra-servicios-fallback.util';

/** Logger mínimo que necesita la normalización V2 para avisar respuestas vacías. */
export interface NormalizeLogger {
    warn: (message: string, context?: string) => void;
}

/**
 * Operador RxJS para normalizar respuesta V1
 */
export function mapV1ToNormalized() {
    return (
        source: Observable<any>
    ): Observable<{
        data: ExcelDataNormalized;
        extraServiciosFailed: boolean;
    }> => {
        return new Observable((subscriber) => {
            source.subscribe({
                next: (response) => {
                    const data = response?.data || response;
                    const normalized: ExcelDataNormalized = {
                        contenedores: data.contenedores || { rows: [] },
                        pallets: data.pallets || { rows: [] },
                        serviciosExtra: data.serviciosExtra || { rows: [] },
                        transportes: { rows: [] },
                        totales: data.totales || {
                            totalCntr: 0,
                            totalPlts: 0,
                            totalAgenciamiento: 0,
                            totalTransporte: 0,
                            totalVenta: 0,
                            totalTraspaso: 0,
                            totalGeneral: 0,
                        },
                    };
                    // V1 trae Servicios Extra junto con el resto de la
                    // respuesta (sin llamada separada): no hay un fetch
                    // propio que pueda fallar acá.
                    subscriber.next({
                        data: normalized,
                        extraServiciosFailed: false,
                    });
                },
                error: (err) => subscriber.error(err),
                complete: () => subscriber.complete(),
            });
        });
    };
}

/**
 * Transforma respuesta V2 a estructura normalizada compatible con frontend
 */
export function transformV2ToNormalized(
    response: ExcelV2Response,
    extraServiciosRows: Record<string, string | number | null>[] = [],
    extraServiciosFailed = false,
    logger?: NormalizeLogger
): ExcelDataNormalized {
    const data = response?.data;
    if (!data) {
        logger?.warn('Respuesta V2 sin datos', 'LiquidacionesService');
        return getEmptyNormalizedData();
    }

    const agenciamiento = data.agenciamiento || {
        rows: [],
        totalCntr: 0,
        totalPlts: 0,
    };
    const transportes = data.transportes || {
        rows: [],
        totalVenta: 0,
        totalTraspaso: 0,
    };
    const header = data.header;

    // Mapear filas de agenciamiento - Incluir TODOS los campos del backend V2
    const contenedoresRows: any[] = (agenciamiento.rows || []).map(
        (row: ExcelV2AgenciamientoRow) => ({
            // Identificadores
            // Punto C (OPB, ago-2026): el ORDEN de las columnas del Excel sale del
            // orden de estas claves — la hoja se arma con `Object.keys(primeraFila)`.
            // Los identificadores de documento van primero, igual que en Consolidado
            // y Transportes.
            nroFacturaVenta: row.nroFacturaVenta,
            nroNotaCobro: row.nroNotaCobro,
            nroLiquidacion: row.nroLiquidacion,
            nroIE: row.nroIE,
            numeroBl: row.numeroBl,
            reserva: row.reserva,
            contenedor: row.contenedor,
            cliente: row.cliente,
            refCliente: row.refCliente,
            nroDespacho: row.nroDespacho,
            dus: row.dus,
            estadoDus: row.estadoDus,
            aceptacionDus: row.aceptacionDus,
            prorrogaDus: row.prorrogaDus,
            // Información de embarque
            nave: row.nave,
            nroViaje: row.nroViaje,
            naviera: row.naviera,
            pol: row.pol,
            pod: row.pod,
            paisDestinoFinal: row.paisDestinoFinal,
            etd: row.etd,
            mes: row.mes,
            consignatario: row.consignatario,
            tipoEmbarque: row.tipoEmbarque,
            sConv: row.sConv,
            // Cantidades
            cntrs: row.cntrs,
            tipoBultoCntr: row.tipoBultoCntr,
            plts: row.plts,
            cajas: row.cajas,
            especie: row.especie,
            condicion: row.condicion,
            tipoEmbalaje: row.tipoEmbalaje,
            kilosNetos: row.kilosNetos,
            kilosBrutos: row.kilosBrutos,
            viaTransporte: row.viaTransporte,
            // Montos
            agenciamiento: row.agenciamiento,

            // ── Servicios: columnas DINÁMICAS ──────────────────────────────
            // Acá antes había ~40 líneas copiando campo por campo. Esa lista era
            // la última de cuatro que había que mantener a mano, y la que dejaba
            // fuera lo que las otras tres sí traían: "Diferencia tarifa flete"
            // estaba en la query, en el builder y en el DTO, y NO llegaba al
            // Excel porque nadie la agregó acá (medido en el archivo real de la
            // liq 1581 el 2026-07-28: $189.674 de descuadre). Lo mismo con Gate
            // Out, Gate In, Habilitación SAG y DUS no legalizado.
            //
            // Ahora el backend manda un mapa con los servicios REALES de la
            // liquidación, en orden de presentación, y acá sólo se expande. Un
            // servicio nuevo aparece sin tocar este archivo.
            ...(row.servicios ?? {}),

            gestionOpb: row.gestionOpb,
            // Totales
            totalVenta: row.totalVenta,
            totalTraspaso: row.totalTraspaso,
        })
    );

    // V2: No agregar fila de totales, los totales van en la sección separada

    // Mapear filas de transportes - Incluir TODOS los campos del backend
    const transportesRows: any[] = (transportes.rows || []).map(
        (row: ExcelV2TransporteRow) => ({
            // Punto C (OPB, ago-2026): identificadores de documento primero, igual
            // que en Consolidado y Agenciamiento.
            nroFacturaVenta: row.nroFacturaVenta,
            nroNotaCobro: row.nroNotaCobro,
            liquidacion: row.liquidacion,
            ot: row.ot,
            estado: row.estado,
            cliente: row.cliente,
            exportador: row.exportador,
            booking: row.booking,
            naviera: row.naviera,
            nave: row.nave,
            etd: row.etd,
            mes: row.mes,
            retiroContenedor: row.retiroContenedor,
            deposito: row.deposito,
            lugarOrigen: row.lugarOrigen,
            planta: row.planta,
            pol: row.pol,
            terminal: row.terminal,
            pod: row.pod,
            container: row.container,
            sello: row.sello,
            pallets: row.pallets,
            tipoContenedor: row.tipoContenedor,
            costo: row.costo,
            tipoEmbarque: row.tipoEmbarque,
            shipConv: row.shipConv,
            // Punto C (OPB, ago-2026): se dan de baja "Transport Order ID" y
            // "Container Number".
            // `containerNumber` era un CLON EXACTO de `container` (col. 20): la query
            // proyecta `LTRIM(RTRIM(toi.ContainerNumber))` dos veces, con dos alias
            // distintos (excel-v2-transporte.queries.ts:115 y :129), así que no pueden
            // diferir nunca. Mostraba el mismo contenedor dos veces en la misma hoja.
            // `transportOrderId` es el id interno de la O/T, redundante con "OT" (col. 4)
            // y ya causó el 10.611 que reportó el cliente en TOTALES (liq 1612, jul-2026).
            // El dato sigue llegando del backend; sólo deja de salir en el Excel.

            // ── Servicios: columnas DINÁMICAS ──────────────────────────────
            // Acá había ~37 líneas copiando campo por campo. El censo del
            // 2026-07-28 midió lo que costaba: 24 servicios sin columna por
            // $10.047.762, más DOS columnas declaradas que nunca se llenaban
            // porque el `LIKE` de la query es acento-sensible y no coincidía con
            // el nombre real de la BD ("Generador o Conexión", $2.816.500).
            //
            // Ahora el backend manda los servicios REALES de la liquidación, en
            // orden de presentación, y acá sólo se expanden.
            ...(row.servicios ?? {}),

            gestionOpb: row.gestionOpb,
            totalVenta: row.totalVenta,
            totalTraspaso: row.totalTraspaso,
        })
    );

    // No agregar fila de totales para mantener estructura limpia del V2

    // Mapear filas de consolidado (si existe)
    const consolidadoData = data.consolidado;
    const consolidadoRows: any[] = consolidadoData?.rows
        ? consolidadoData.rows.map((row: any) => ({ ...row }))
        : [];

    return {
        contenedores: {
            rows: contenedoresRows,
            totales: {
                totalCntr: (agenciamiento as any).totalCntr || 0,
                totalPlts: (agenciamiento as any).totalPlts || 0,
                totalAgenciamiento:
                    (agenciamiento as any).totalAgenciamiento || 0,
                totalDusLegalizado:
                    (agenciamiento as any).totalDusLegalizado || 0,
                totalSecurityFee: (agenciamiento as any).totalSecurityFee || 0,
                totalVenta: (agenciamiento as any).totalVenta || 0,
                totalTraspaso: (agenciamiento as any).totalTraspaso || 0,
            },
            serviciosPresentes: (agenciamiento as any).serviciosPresentes || [],
        },
        pallets: { rows: [] }, // V2 solo tiene 2 pestañas: agenciamiento y transportes
        // MEJ-01 (D7): llenado por `getLiquidacionAgenciaExcelDataNormalized`
        // desde el batch `ids=[id]`; ver `getExtraServiciosTabBatch`.
        // RESILIENCIA (2026-10-03): si el fetch falló, `resolveServiciosExtraRows`
        // reemplaza las filas por un aviso visible — nunca una hoja vacía en silencio.
        serviciosExtra: {
            rows: resolveServiciosExtraRows(
                extraServiciosRows,
                extraServiciosFailed
            ),
        },
        transportes: {
            rows: transportesRows,
            totales: {
                totalCosto: (transportes as any).totalCosto || 0,
                totalVenta: (transportes as any).totalVenta || 0,
                totalTraspaso: (transportes as any).totalTraspaso || 0,
            },
            serviciosPresentes: (transportes as any).serviciosPresentes || [],
        },
        consolidado:
            consolidadoRows.length > 0
                ? {
                      rows: consolidadoRows,
                      totales: {
                          totalContenedores:
                              consolidadoData?.totalContenedores || 0,
                          totalVentaAgenciamiento:
                              consolidadoData?.totalVentaAgenciamiento || 0,
                          totalTraspasoAgenciamiento:
                              consolidadoData?.totalTraspasoAgenciamiento || 0,
                          totalVentaTransporte:
                              consolidadoData?.totalVentaTransporte || 0,
                          totalTraspasoTransporte:
                              consolidadoData?.totalTraspasoTransporte || 0,
                          totalVenta: consolidadoData?.totalVenta || 0,
                          totalTraspaso: consolidadoData?.totalTraspaso || 0,
                          totalGeneral: consolidadoData?.totalGeneral || 0,
                      },
                  }
                : undefined,
        totales: {
            totalCntr: header?.totalCntr || agenciamiento.totalCntr || 0,
            totalPlts: header?.totalPlts || agenciamiento.totalPlts || 0,
            totalAgenciamiento: agenciamiento.totalAgenciamiento || 0,
            totalTransporte: transportes.totalCosto || 0,
            totalVenta:
                (agenciamiento.totalVenta || 0) + (transportes.totalVenta || 0),
            totalTraspaso:
                (agenciamiento.totalTraspaso || 0) +
                (transportes.totalTraspaso || 0),
            totalGeneral: header?.totalClp || 0,
        },
        header: header,
    };
}

/**
 * Retorna estructura normalizada vacía
 */
export function getEmptyNormalizedData(): ExcelDataNormalized {
    return {
        contenedores: { rows: [] },
        pallets: { rows: [] },
        serviciosExtra: { rows: [] },
        transportes: { rows: [] },
        totales: {
            totalCntr: 0,
            totalPlts: 0,
            totalAgenciamiento: 0,
            totalTransporte: 0,
            totalVenta: 0,
            totalTraspaso: 0,
            totalGeneral: 0,
        },
    };
}
